import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MonthNav } from "@/components/fuel/MonthNav";
import { MonthSummary } from "@/components/fuel/MonthSummary";
import { FuelStatsMonthly } from "@/components/fuel/FuelStatsMonthly";
import { FuelAlertsPanel } from "@/components/fuel/FuelAlertsPanel";
import { currentMonth } from "@/components/fuel/monthUtils";
import {
  GasPump,
  Plus,
  Warning,
  TrashSimple,
  PencilSimple,
  X,
  CreditCard,
  Camera,
  Check,
  CheckSquare,
  Square,
  ArrowClockwise,
  CaretUpDown,
  ChartLineUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  fetchFuelCards,
  createFuelCard,
  updateFuelCard,
  deleteFuelCard,
  resetFuelCardChecks,
  fetchFuelEntries,
  createFuelEntry,
  updateFuelEntry,
  deleteFuelEntry,
  fetchFuelAnalytics,
  fetchFuelSettings,
  updateFuelSettings,
  fetchTrucks,
  fetchDrivers,
  fetchAssignments,
} from "@/lib/api";
import { useAutoRefresh } from "@/lib/useAutoRefresh";

const todayIso = () => new Date().toISOString().slice(0, 10);
const currentTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const SITE_OPTIONS = [
  { value: "all", label: "Tous sites" },
  { value: "DLP4", label: "DLP4" },
  { value: "DWB9", label: "DWB9" },
];

export default function ConsommationPage() {
  const [tab, setTab] = useState("entries"); // entries | cards | stats
  const [siteFilter, setSiteFilter] = useState("all");
  const [entries, setEntries] = useState([]);
  const [cards, setCards] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [month, setMonth] = useState(currentMonth());
  const [analytics, setAnalytics] = useState(null);
  const [threshold, setThreshold] = useState(15);
  const [loading, setLoading] = useState(true);

  const loadAnalytics = useCallback(async () => {
    try {
      setAnalytics(await fetchFuelAnalytics(month, siteFilter));
    } catch {
      toast.error("Erreur de calcul des statistiques");
    }
  }, [month, siteFilter]);
  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [e, c, t, d, sett] = await Promise.all([
        fetchFuelEntries(),
        fetchFuelCards(),
        fetchTrucks(),
        fetchDrivers(),
        fetchFuelSettings(),
      ]);
      setEntries(e);
      setCards(c);
      setTrucks(t);
      setDrivers(d);
      setThreshold(sett.threshold_l_per_100);
      loadAnalytics();
    } catch {
      if (!silent) toast.error("Erreur de chargement");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  useAutoRefresh(() => load({ silent: true }), 20000);

  const truckById = useMemo(() => {
    const m = {};
    for (const t of trucks) m[t.id] = t;
    return m;
  }, [trucks]);
  const cardById = useMemo(() => {
    const m = {};
    for (const c of cards) m[c.id] = c;
    return m;
  }, [cards]);
  const alertsByTruck = useMemo(() => {
    const m = {};
    for (const a of analytics?.alerts.trucks || []) m[a.truck_id] = { ...a, is_suspect: true };
    return m;
  }, [analytics]);

  const alertCount = (analytics?.alerts.trucks.length || 0) + (analytics?.alerts.drivers.length || 0);
  const monthEntries = useMemo(
    () => entries.filter((e) => (e.date || "").startsWith(month) && (siteFilter === "all" || truckById[e.truck_id]?.site === siteFilter)),
    [entries, month, siteFilter, truckById]
  );

  return (
    <div className="max-w-7xl mx-auto p-4">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-widest text-zinc-500">NY Trans</div>
        <h1 className="text-3xl font-bold serif inline-flex items-center gap-2">
          <GasPump size={26} weight="fill" className="text-amber-600" /> Consommation
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-3 border-b-2 border-zinc-900" data-testid="fuel-tabs">
        {[
          { k: "entries", label: `Historique (${monthEntries.length})` },
          { k: "cards", label: `Cartes (${cards.length})` },
          { k: "stats", label: "Statistiques" },
          { k: "alerts", label: `Alertes${alertCount > 0 ? ` · ⚠ ${alertCount}` : ""}` },
        ].map(({ k, label }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border-2 border-b-0 ${
              tab === k
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
            }`}
            data-testid={`fuel-tab-${k}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Site selector */}
      <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="fuel-site-selector">
        <span className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">
          Site courant
        </span>
        {SITE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setSiteFilter(opt.value)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border-2 ${
              siteFilter === opt.value
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-900"
            }`}
            data-testid={`fuel-site-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
        {siteFilter !== "all" && (
          <span className="text-[10px] text-zinc-500 italic ml-1">
            Camions et chauffeurs du site {siteFilter} en priorité, l&apos;autre site reste sélectionnable avec alerte.
          </span>
        )}
      </div>

      {(tab === "entries" || tab === "stats") && (
        <div className="mb-4">
          <MonthNav month={month} onChange={setMonth} />
        </div>
      )}

      {loading && <div className="text-center py-10 text-zinc-500">Chargement…</div>}

      {!loading && tab === "entries" && (
        <EntriesPanel
          entries={monthEntries}
          analytics={analytics}
          trucks={trucks}
          drivers={drivers}
          cards={cards}
          truckById={truckById}
          cardById={cardById}
          alertsByTruck={alertsByTruck}
          threshold={analytics?.threshold_l_per_100 ?? threshold}
          siteFilter={siteFilter}
          onChanged={load}
        />
      )}
      {!loading && tab === "cards" && (
        <CardsPanel cards={cards} trucks={trucks} drivers={drivers} truckById={truckById} onChanged={load} />
      )}
      {!loading && tab === "stats" && (
        <FuelStatsMonthly
          data={analytics}
          threshold={threshold}
          onThresholdChange={setThreshold}
          onSaveThreshold={async () => {
            try {
              await updateFuelSettings(threshold);
              toast.success(`Seuil mis à jour : ${threshold} L/100km`);
              loadAnalytics();
            } catch {
              toast.error("Erreur");
            }
          }}
        />
      )}
      {!loading && tab === "alerts" && (
        <FuelAlertsPanel alerts={analytics?.alerts} threshold={analytics?.threshold_l_per_100 ?? threshold} />
      )}
    </div>
  );
}

/* ===================== ENTRIES ===================== */

function EntriesPanel({ entries, analytics, trucks, drivers, cards, truckById, cardById, alertsByTruck, threshold, siteFilter, onChanged }) {
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [filterOver, setFilterOver] = useState(false);
  const [filterTruck, setFilterTruck] = useState("all");
  const [filterCard, setFilterCard] = useState("all");
  const openNew = () => setDialog({ open: true, editing: null });
  const openEdit = (e) => setDialog({ open: true, editing: e });

  const perEntry = analytics?.per_entry || {};

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (filterTruck !== "all" && e.truck_id !== filterTruck) return false;
      if (filterCard !== "all") {
        if (filterCard === "__none__") {
          if (e.card_id) return false;
        } else if (e.card_id !== filterCard) {
          return false;
        }
      }
      if (filterOver && !perEntry[e.id]?.over) return false;
      if (siteFilter !== "all") {
        const t = truckById[e.truck_id];
        if (!t || t.site !== siteFilter) return false;
      }
      return true;
    });
  }, [entries, filterOver, filterTruck, filterCard, perEntry, siteFilter, truckById]);

  const overCount = entries.filter((e) => perEntry[e.id]?.over).length;

  return (
    <div>
      <MonthSummary summary={analytics?.summary} threshold={threshold} />
      {/* Filters + Action */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setFilterOver((v) => !v)}
          className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border ${
            filterOver
              ? "bg-red-600 text-white border-red-600"
              : "bg-white text-zinc-900 border-zinc-300 hover:border-red-600"
          }`}
          data-testid="fuel-filter-over"
          title="N'afficher que les pleins > seuil"
        >
          <Warning size={12} weight="bold" className="inline mr-1" />
          Surconsommation {overCount > 0 && `(${overCount})`}
        </button>

        <FilterTruckPicker
          value={filterTruck}
          trucks={trucks}
          siteFilter={siteFilter}
          onChange={setFilterTruck}
        />

        <FilterCardPicker
          value={filterCard}
          cards={cards}
          onChange={setFilterCard}
        />

        <span className="ml-1 text-[10px] uppercase tracking-widest text-zinc-500 mono">
          {filtered.length} / {entries.length} plein{entries.length > 1 ? "s" : ""}
        </span>

        <button
          onClick={openNew}
          className="ml-auto px-3 py-2 text-xs font-bold uppercase tracking-widest border border-amber-600 bg-amber-600 text-white hover:bg-amber-700 inline-flex items-center gap-2"
          data-testid="fuel-new-entry"
        >
          <Plus size={14} weight="bold" /> Nouveau plein
        </button>
      </div>

      {entries.length === 0 && (
        <div className="text-center py-16 text-zinc-500 border border-dashed border-zinc-300">
          <GasPump size={32} weight="duotone" className="mx-auto mb-2" />
          <div className="text-sm font-semibold">Aucun plein ce mois-ci</div>
          <div className="text-xs mt-1">Changez de mois avec les flèches ou cliquez sur &quot;Nouveau plein&quot;.</div>
        </div>
      )}
      {entries.length > 0 && filtered.length === 0 && (
        <div className="text-center py-10 text-zinc-500 border border-dashed border-zinc-300 text-sm">
          Aucun plein pour ce filtre.
        </div>
      )}

      <div className="space-y-1.5">
        {filtered.map((e) => {
          const t = truckById[e.truck_id];
          const c = e.card_id ? cardById[e.card_id] : null;
          const alert = alertsByTruck[e.truck_id];
          const perEntryData = perEntry[e.id];
          const isOver = perEntryData?.over;
          const priceL = e.liters > 0 ? (e.amount_eur / e.liters).toFixed(2) : null;
          return (
            <div
              key={e.id}
              className={`border p-3 cursor-pointer hover:bg-zinc-50 ${
                isOver ? "bg-red-50 border-red-500" : "bg-white border-zinc-200"
              }`}
              onClick={() => openEdit(e)}
              data-testid={`fuel-entry-${e.id}`}
            >
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold mono uppercase text-base">
                      {t?.license_plate || "?"}
                    </span>
                    <span className="text-xs text-zinc-500">{t?.brand} · {t?.site}</span>
                    {perEntryData?.aberrant && (
                      <span
                        className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-zinc-500 text-white"
                        title={perEntryData.aberrant_reason}
                        data-testid={`fuel-entry-aberrant-${e.id}`}
                      >
                        {perEntryData.l_per_100.toFixed(2)} L/100 · aberrant, exclu
                      </span>
                    )}
                    {perEntryData && !perEntryData.aberrant && (
                      <span
                        className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1 ${
                          isOver
                            ? "bg-red-600 text-white"
                            : "bg-emerald-600 text-white"
                        }`}
                        data-testid={`fuel-entry-l100-${e.id}`}
                        title={`${perEntryData.delta_km} km parcourus depuis le plein précédent`}
                      >
                        {isOver && <Warning size={10} weight="fill" />}
                        {perEntryData.l_per_100.toFixed(2)} L/100
                      </span>
                    )}
                    {!perEntryData && (
                      <span
                        className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-zinc-200 text-zinc-500"
                        title="Premier plein enregistré — pas de calcul possible"
                      >
                        1er plein
                      </span>
                    )}
                    {alert?.is_suspect && !isOver && (
                      <span
                        className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-amber-100 text-amber-800 inline-flex items-center gap-1"
                        title={`Moyenne des derniers pleins : ${alert.avg_l_per_100} L/100`}
                      >
                        Camion en alerte (derniers pleins)
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-600 mt-0.5">
                    <span className="font-bold">{e.date?.slice(0, 10)}</span>
                    {e.fuel_time && (
                      <span className="ml-1 mono text-zinc-500">{e.fuel_time}</span>
                    )}
                    <span className="mx-1">·</span>
                    {e.driver_name || "— chauffeur ? —"}
                    {c && (
                      <span className="ml-1 text-zinc-400">
                        · {c.card_number}
                        {c.status === "opposition" && (
                          <span className="ml-1 text-red-600 font-bold">OPPOSITION</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right text-xs">
                  <div className="mono font-bold">
                    {e.liters.toFixed(2)} L · {e.odometer_km.toLocaleString("fr-FR")} km
                  </div>
                  {e.amount_eur > 0 && (
                    <div className="mono text-zinc-500">
                      {e.amount_eur.toFixed(2)} €
                      {priceL && <span className="ml-1">({priceL} €/L)</span>}
                    </div>
                  )}
                  <div className="mt-1 flex items-center gap-1 justify-end flex-wrap">
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold ${
                        e.photo_confirmed
                          ? "bg-emerald-600 text-white"
                          : "bg-zinc-200 text-zinc-600"
                      }`}
                    >
                      <Camera size={10} weight="bold" />
                      {e.photo_confirmed ? "Photo OK" : "Photo ?"}
                    </span>
                    {e.is_full_tank && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold bg-blue-600 text-white"
                        title="Plein complet"
                      >
                        <GasPump size={10} weight="fill" />
                        Plein complet
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {dialog.open && (
        <FuelEntryDialog
          open={dialog.open}
          editing={dialog.editing}
          trucks={trucks}
          drivers={drivers}
          cards={cards}
          threshold={threshold}
          alertsByTruck={alertsByTruck}
          siteFilter={siteFilter}
          onClose={() => setDialog({ open: false, editing: null })}
          onSaved={() => {
            setDialog({ open: false, editing: null });
            onChanged();
          }}
        />
      )}
    </div>
  );
}

/* ===================== NEW/EDIT FUEL ENTRY ===================== */

function FuelEntryDialog({ open, editing, trucks, drivers, cards, threshold, alertsByTruck, siteFilter, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    truck_id: editing?.truck_id || "",
    driver_name: editing?.driver_name || "",
    card_id: editing?.card_id || "",
    date: editing?.date ? editing.date.slice(0, 10) : todayIso(),
    fuel_time: editing?.fuel_time || (editing ? "" : currentTime()),
    liters: editing?.liters ?? "",
    odometer_km: editing?.odometer_km ?? "",
    amount_eur: editing?.amount_eur ?? "",
    photo_confirmed: editing?.photo_confirmed || false,
    is_full_tank: editing?.is_full_tank || false,
    notes: editing?.notes || "",
  }));

  // Auto-fill driver ↔ truck based on the SELECTED date's assignments (not today's).
  // Fires whenever truck_id, driver_name OR date change. Fills the missing side
  // (driver or truck) from the daily_assignments matching that exact date.
  // Card autofill is triggered only when a truck is picked and no card is set.
  const [autoHint, setAutoHint] = useState(""); // small message under the date field
  useEffect(() => {
    if (editing) return; // do not override user edits when modifying
    if (!form.date) return;
    // Nothing to look up if BOTH truck and driver are empty
    if (!form.truck_id && !form.driver_name) {
      setAutoHint("");
      return;
    }
    let cancelled = false;
    const applyDefaults = async () => {
      try {
        const assigns = await fetchAssignments(form.date, null);
        if (cancelled) return;
        const rows = assigns || [];

        // 1) truck known, driver empty → fill driver
        if (form.truck_id && !form.driver_name) {
          const match = rows.find((a) => a.truck_id === form.truck_id);
          if (match && match.driver_name) {
            setForm((f) => (f.driver_name ? f : { ...f, driver_name: match.driver_name }));
            setAutoHint(
              `Chauffeur pré-rempli : ${match.driver_name} (attribué à ce camion le ${form.date})`
            );
          } else {
            setAutoHint(
              `Aucune attribution enregistrée pour ce camion le ${form.date} — merci de sélectionner le chauffeur manuellement.`
            );
          }
        }
        // 2) driver known, truck empty → fill truck
        else if (form.driver_name && !form.truck_id) {
          const match = rows.find(
            (a) => a.driver_name === form.driver_name && a.truck_id
          );
          if (match && match.truck_id) {
            setForm((f) => (f.truck_id ? f : { ...f, truck_id: match.truck_id }));
            setAutoHint(
              `Camion pré-rempli : ${match.truck_plate || ""} (attribué à ${form.driver_name} le ${form.date})`
            );
          } else {
            setAutoHint(
              `Aucune attribution enregistrée pour ${form.driver_name} le ${form.date} — merci de sélectionner le camion manuellement.`
            );
          }
        }
        // 3) both set: verify coherence and warn if mismatch
        else if (form.truck_id && form.driver_name) {
          const truckMatch = rows.find((a) => a.truck_id === form.truck_id);
          if (truckMatch && truckMatch.driver_name && truckMatch.driver_name !== form.driver_name) {
            setAutoHint(
              `⚠ Attention : le ${form.date}, ce camion était attribué à ${truckMatch.driver_name}, pas à ${form.driver_name}.`
            );
          } else {
            setAutoHint("");
          }
        }
      } catch {
        setAutoHint("");
      }

      // Card autofill: prefer the card assigned to the DRIVER (personal card),
      // otherwise fall back to the card assigned to the TRUCK. Only applies
      // when no card has been picked yet.
      if (!form.card_id) {
        let assignedCard = null;
        if (form.driver_name) {
          assignedCard = cards.find(
            (c) => c.assigned_driver_name === form.driver_name && c.status === "actif"
          );
        }
        if (!assignedCard && form.truck_id) {
          assignedCard = cards.find(
            (c) => c.assigned_truck_id === form.truck_id && c.status === "actif"
          );
        }
        if (assignedCard) {
          setForm((f) => (f.card_id ? f : { ...f, card_id: assignedCard.id }));
        }
      }
    };
    applyDefaults();
    return () => {
      cancelled = true;
    };
  }, [form.truck_id, form.driver_name, form.date]);

  const save = async () => {
    if (!form.truck_id) return toast.error("Sélectionner un camion");
    if (!form.driver_name) return toast.error("Sélectionner un chauffeur");
    if (!form.liters || parseFloat(form.liters) <= 0) return toast.error("Litres requis");
    if (!form.odometer_km || parseInt(form.odometer_km) <= 0)
      return toast.error("Kilométrage requis");
    const body = {
      truck_id: form.truck_id,
      driver_name: form.driver_name || "",
      card_id: form.card_id || null,
      date: new Date(form.date + "T12:00:00").toISOString(),
      fuel_time: form.fuel_time || "",
      liters: parseFloat(form.liters),
      odometer_km: parseInt(form.odometer_km),
      amount_eur: parseFloat(form.amount_eur || "0") || 0,
      photo_confirmed: !!form.photo_confirmed,
      is_full_tank: !!form.is_full_tank,
      notes: form.notes || "",
    };
    try {
      if (editing) await updateFuelEntry(editing.id, body);
      else await createFuelEntry(body);
      toast.success(editing ? "Plein mis à jour" : "Plein enregistré");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm("Supprimer ce plein ?")) return;
    try {
      await deleteFuelEntry(editing.id);
      toast.success("Plein supprimé");
      onSaved();
    } catch {
      toast.error("Erreur");
    }
  };

  const suspectHint = form.truck_id ? alertsByTruck[form.truck_id] : null;
  const selectedTruck = trucks.find((t) => t.id === form.truck_id);
  const selectedDriver = drivers.find((d) => d.name === form.driver_name);

  // Cross-site detection
  const truckOffSite =
    siteFilter !== "all" && selectedTruck && selectedTruck.site && selectedTruck.site !== siteFilter;
  const driverOffSite =
    siteFilter !== "all" &&
    selectedDriver &&
    selectedDriver.site &&
    selectedDriver.site !== "ALL" &&
    selectedDriver.site !== siteFilter;
  const truckDriverMismatch =
    selectedTruck &&
    selectedDriver &&
    selectedTruck.site &&
    selectedDriver.site &&
    selectedDriver.site !== "ALL" &&
    selectedTruck.site !== selectedDriver.site;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border border-zinc-900 max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-lg font-bold">
            <GasPump size={20} weight="fill" className="text-amber-600" />
            {editing ? "Modifier le plein" : "Nouveau plein"}
            {siteFilter !== "all" && (
              <span
                className="ml-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-zinc-900 text-white"
                data-testid="fuel-dialog-site-badge"
              >
                Site {siteFilter}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="label-tag">Camion *</Label>
            <TruckPicker
              value={form.truck_id}
              trucks={trucks}
              siteFilter={siteFilter}
              onChange={(v) => setForm({ ...form, truck_id: v })}
            />
            {truckOffSite && (
              <div
                className="mt-2 border-2 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900 inline-flex items-center gap-2"
                data-testid="fuel-truck-offsite-warning"
              >
                <Warning size={14} weight="fill" /> Camion du site{" "}
                <b>{selectedTruck.site}</b> — hors du site courant {siteFilter}.
              </div>
            )}
            {suspectHint?.is_suspect && (
              <div data-testid="fuel-truck-suspect-warning" className="mt-2 border-2 border-red-500 bg-red-50 px-3 py-2 text-xs text-red-800 inline-flex items-center gap-2">
                <Warning size={14} weight="fill" /> Ce camion a une conso moyenne de{" "}
                <b className="mono">{suspectHint.avg_l_per_100} L/100</b> sur ses derniers pleins (seuil {threshold}) —
                suspicion de vol
              </div>
            )}
          </div>

          <div>
            <Label className="label-tag">Chauffeur *</Label>
            <DriverPicker
              value={form.driver_name}
              drivers={drivers}
              siteFilter={siteFilter}
              onChange={(v) => setForm({ ...form, driver_name: v })}
            />
            {driverOffSite && (
              <div
                className="mt-2 border-2 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900 inline-flex items-center gap-2"
                data-testid="fuel-driver-offsite-warning"
              >
                <Warning size={14} weight="fill" /> Chauffeur du site{" "}
                <b>{selectedDriver.site}</b> — hors du site courant {siteFilter}.
              </div>
            )}
            {truckDriverMismatch && !truckOffSite && !driverOffSite && (
              <div
                className="mt-2 border-2 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900 inline-flex items-center gap-2"
                data-testid="fuel-cross-site-warning"
              >
                <Warning size={14} weight="fill" /> Camion ({selectedTruck.site}) et chauffeur (
                {selectedDriver.site}) ne sont pas sur le même site.
              </div>
            )}
          </div>

          {autoHint && !editing && (
            <div
              className={`text-[11px] px-2 py-1.5 border ${
                autoHint.startsWith("⚠")
                  ? "border-amber-400 bg-amber-50 text-amber-800"
                  : autoHint.startsWith("Aucune")
                  ? "border-zinc-300 bg-zinc-50 text-zinc-600"
                  : "border-emerald-300 bg-emerald-50 text-emerald-800"
              }`}
              data-testid="fuel-entry-auto-hint"
            >
              {autoHint}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="label-tag">Date *</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="rounded-none border-zinc-900"
                data-testid="fuel-entry-date"
              />
            </div>
            <div>
              <Label className="label-tag">Heure</Label>
              <Input
                type="time"
                value={form.fuel_time}
                onChange={(e) => setForm({ ...form, fuel_time: e.target.value })}
                className="rounded-none border-zinc-900 mono"
                data-testid="fuel-entry-time"
              />
            </div>
            <div>
              <Label className="label-tag">Montant (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.amount_eur}
                onChange={(e) => setForm({ ...form, amount_eur: e.target.value })}
                className="rounded-none border-zinc-900 mono"
                data-testid="fuel-entry-amount"
              />
            </div>
          </div>

          <div>
            <Label className="label-tag">Carte utilisée</Label>
            <CardPicker
              value={form.card_id}
              cards={cards}
              onChange={(v) => setForm({ ...form, card_id: v })}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-tag">Litres *</Label>
              <Input
                type="number"
                step="0.01"
                value={form.liters}
                onChange={(e) => setForm({ ...form, liters: e.target.value })}
                className="rounded-none border-zinc-900 mono"
                data-testid="fuel-entry-liters"
              />
            </div>
            <div>
              <Label className="label-tag">Km actuel *</Label>
              <Input
                type="number"
                value={form.odometer_km}
                onChange={(e) => setForm({ ...form, odometer_km: e.target.value })}
                className="rounded-none border-zinc-900 mono"
                data-testid="fuel-entry-odometer"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label
              className="flex items-center gap-2 px-3 py-2 border-2 border-zinc-300 bg-white cursor-pointer hover:border-zinc-900"
              data-testid="fuel-entry-photo-toggle"
            >
              <input
                type="checkbox"
                checked={form.photo_confirmed}
                onChange={(e) => setForm({ ...form, photo_confirmed: e.target.checked })}
                className="w-4 h-4"
              />
              <Camera size={16} weight="bold" />
              <span className="text-xs uppercase tracking-widest font-bold">
                Photo envoyée
              </span>
            </label>

            <label
              className={`flex items-center gap-2 px-3 py-2 border-2 cursor-pointer ${
                form.is_full_tank
                  ? "border-emerald-600 bg-emerald-50"
                  : "border-zinc-300 bg-white hover:border-zinc-900"
              }`}
              data-testid="fuel-entry-fulltank-toggle"
            >
              <input
                type="checkbox"
                checked={form.is_full_tank}
                onChange={(e) => setForm({ ...form, is_full_tank: e.target.checked })}
                className="w-4 h-4"
              />
              <GasPump size={16} weight="fill" />
              <span className="text-xs uppercase tracking-widest font-bold">
                Plein complet
              </span>
            </label>
          </div>

          <div>
            <Label className="label-tag">Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="rounded-none border-zinc-900"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          {editing && (
            <button
              onClick={remove}
              className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-red-600 text-red-600 hover:bg-red-600 hover:text-white inline-flex items-center gap-2 mr-auto"
              data-testid="fuel-entry-delete"
            >
              <TrashSimple size={14} weight="bold" /> Supprimer
            </button>
          )}
          <button
            onClick={onClose}
            className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-300 hover:border-zinc-900 inline-flex items-center gap-2"
          >
            <X size={14} weight="bold" /> Annuler
          </button>
          <button
            onClick={save}
            className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 inline-flex items-center gap-2"
            data-testid="fuel-entry-save"
          >
            <PencilSimple size={14} weight="bold" /> {editing ? "Mettre à jour" : "Enregistrer"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ===================== CARDS ===================== */

function CardsPanel({ cards, trucks, drivers, truckById, onChanged }) {
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [savingId, setSavingId] = useState(null);
  const [resetting, setResetting] = useState(false);
  const openNew = () => setDialog({ open: true, editing: null });
  const openEdit = (c) => setDialog({ open: true, editing: c });

  const checkedCount = useMemo(
    () => cards.filter((c) => c.checked_in_hand).length,
    [cards]
  );
  const total = cards.length;
  const allChecked = total > 0 && checkedCount === total;

  const toggleCheck = async (card, next) => {
    setSavingId(card.id);
    try {
      await updateFuelCard(card.id, { checked_in_hand: next });
      await onChanged();
    } catch (err) {
      toast.error("Impossible de mettre à jour la carte");
      console.error(err);
    } finally {
      setSavingId(null);
    }
  };

  const resetAll = async () => {
    if (checkedCount === 0) return;
    if (
      !window.confirm(
        `Réinitialiser le contrôle ? Les ${checkedCount} carte${
          checkedCount > 1 ? "s" : ""
        } cochée${checkedCount > 1 ? "s" : ""} seront décochées.`
      )
    )
      return;
    setResetting(true);
    try {
      await resetFuelCardChecks();
      await onChanged();
      toast.success("Contrôle réinitialisé");
    } catch (err) {
      toast.error("Erreur lors de la réinitialisation");
      console.error(err);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div>
      {/* Header : contrôle physique + action */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {total > 0 && (
          <>
            <div
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest border inline-flex items-center gap-2 ${
                allChecked
                  ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                  : "border-zinc-300 bg-white text-zinc-900"
              }`}
              data-testid="fuel-cards-check-counter"
              title="Compteur de cartes physiquement contrôlées"
            >
              <CheckSquare
                size={14}
                weight={allChecked ? "fill" : "regular"}
                className={allChecked ? "text-emerald-600" : "text-zinc-500"}
              />
              <span>Contrôlées</span>
              <span className="mono">
                {checkedCount} / {total}
              </span>
              {!allChecked && checkedCount > 0 && (
                <span className="text-[10px] text-zinc-500 normal-case tracking-normal">
                  ({total - checkedCount} restante{total - checkedCount > 1 ? "s" : ""})
                </span>
              )}
              {allChecked && (
                <span className="text-[10px] text-emerald-700 normal-case tracking-normal">
                  Toutes vérifiées
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={resetAll}
              disabled={checkedCount === 0 || resetting}
              className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest border border-zinc-300 bg-white text-zinc-900 hover:border-red-600 hover:text-red-600 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-zinc-300 disabled:hover:text-zinc-900"
              data-testid="fuel-cards-reset-checks"
              title="Décocher toutes les cartes pour démarrer un nouveau contrôle"
            >
              <ArrowClockwise size={12} weight="bold" />
              {resetting ? "…" : "Réinitialiser"}
            </button>
          </>
        )}
        <button
          onClick={openNew}
          className="ml-auto px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 inline-flex items-center gap-2"
          data-testid="fuel-new-card"
        >
          <Plus size={14} weight="bold" /> Nouvelle carte
        </button>
      </div>

      {cards.length === 0 && (
        <div className="text-center py-16 text-zinc-500 border border-dashed border-zinc-300">
          <CreditCard size={32} weight="duotone" className="mx-auto mb-2" />
          <div className="text-sm font-semibold">Aucune carte enregistrée</div>
          <div className="text-xs mt-1">Ajoute tes cartes Total pour commencer à tracer.</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {cards.map((c) => {
          const t = c.assigned_truck_id ? truckById[c.assigned_truck_id] : null;
          const isChecked = !!c.checked_in_hand;
          const isSaving = savingId === c.id;
          return (
            <div
              key={c.id}
              className={`p-3 border cursor-pointer hover:bg-zinc-50 transition-colors ${
                c.status === "opposition"
                  ? "bg-red-50 border-red-500"
                  : isChecked
                  ? "bg-emerald-50 border-emerald-500"
                  : "bg-white border-zinc-300"
              }`}
              onClick={() => openEdit(c)}
              data-testid={`fuel-card-${c.id}`}
            >
              <div className="flex items-start gap-3">
                {/* Physical check checkbox */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isSaving) toggleCheck(c, !isChecked);
                  }}
                  disabled={isSaving}
                  className={`shrink-0 mt-0.5 w-6 h-6 border-2 flex items-center justify-center transition-colors ${
                    isChecked
                      ? "border-emerald-600 bg-emerald-600 text-white"
                      : "border-zinc-400 bg-white hover:border-emerald-600"
                  } disabled:opacity-50`}
                  data-testid={`fuel-card-check-${c.id}`}
                  title={
                    isChecked
                      ? `Contrôlée${c.checked_by ? ` par ${c.checked_by}` : ""}${
                          c.checked_at ? ` le ${c.checked_at.slice(0, 10)}` : ""
                        }`
                      : "Cocher : je tiens cette carte en main"
                  }
                  aria-pressed={isChecked}
                >
                  {isChecked ? (
                    <Check size={14} weight="bold" />
                  ) : (
                    <Square size={12} weight="bold" className="opacity-0" />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-base font-bold mono flex items-center gap-2">
                        <CreditCard size={18} weight="fill" className="text-amber-600" />
                        {c.card_number}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {t
                          ? `Camion · ${t.license_plate} · ${t.brand}`
                          : "Non assignée à un camion"}
                      </div>
                      {c.assigned_driver_name && (
                        <div
                          className="text-xs text-zinc-700 mt-0.5 inline-flex items-center gap-1"
                          data-testid={`fuel-card-driver-${c.id}`}
                        >
                          <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-zinc-900 text-white">
                            Chauffeur
                          </span>
                          <span className="uppercase font-bold">{c.assigned_driver_name}</span>
                        </div>
                      )}
                      {c.notes && (
                        <div className="text-xs text-zinc-500 mt-1 italic">{c.notes}</div>
                      )}
                      {isChecked && c.checked_at && (
                        <div
                          className="text-[10px] text-emerald-700 mt-1 uppercase tracking-widest font-bold inline-flex items-center gap-1"
                          data-testid={`fuel-card-checked-meta-${c.id}`}
                        >
                          <Check size={10} weight="bold" />
                          Contrôlée · {c.checked_at.slice(0, 10)}
                          {c.checked_by ? ` · ${c.checked_by}` : ""}
                        </div>
                      )}
                    </div>
                    <span
                      className={`shrink-0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${
                        c.status === "actif"
                          ? "bg-emerald-600 text-white"
                          : "bg-red-600 text-white"
                      }`}
                    >
                      {c.status === "actif" ? "Actif" : "Opposition"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {dialog.open && (
        <FuelCardDialog
          open={dialog.open}
          editing={dialog.editing}
          cards={cards}
          trucks={trucks}
          drivers={drivers}
          onClose={() => setDialog({ open: false, editing: null })}
          onSaved={() => {
            setDialog({ open: false, editing: null });
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function FuelCardDialog({ open, editing, cards = [], trucks, drivers = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    card_number: editing?.card_number || "",
    status: editing?.status || "actif",
    assigned_truck_id: editing?.assigned_truck_id || "",
    assigned_driver_name: editing?.assigned_driver_name || "",
    notes: editing?.notes || "",
  });

  // Normalise for similarity comparisons (case + spaces + separators removed).
  const norm = (s) =>
    (s || "").toUpperCase().replace(/[\s\-_.·/]+/g, "").replace(/[^A-Z0-9]/g, "");

  const inputNormalized = useMemo(() => norm(form.card_number), [form.card_number]);

  // Exact-duplicate match on normalized card number (excluding the currently edited one).
  const exactDuplicate = useMemo(() => {
    if (!inputNormalized) return null;
    return cards.find(
      (c) => c.id !== editing?.id && norm(c.card_number) === inputNormalized
    );
  }, [cards, inputNormalized, editing]);

  // Similar (substring both ways, min 3 chars). Skip if exact match already flagged.
  const similarCards = useMemo(() => {
    if (!inputNormalized || inputNormalized.length < 3 || exactDuplicate) return [];
    return cards.filter((c) => {
      if (c.id === editing?.id) return false;
      const n = norm(c.card_number);
      if (!n || n === inputNormalized) return false;
      return (
        (n.includes(inputNormalized) || inputNormalized.includes(n)) &&
        Math.abs(n.length - inputNormalized.length) <= 3
      );
    });
  }, [cards, inputNormalized, exactDuplicate, editing]);

  // Truck already assigned to another active card
  const truckConflict = useMemo(() => {
    if (!form.assigned_truck_id) return null;
    return cards.find(
      (c) =>
        c.id !== editing?.id &&
        c.assigned_truck_id === form.assigned_truck_id &&
        c.status !== "opposition"
    );
  }, [cards, form.assigned_truck_id, editing]);

  // Driver already assigned to another active card
  const driverConflict = useMemo(() => {
    if (!form.assigned_driver_name) return null;
    return cards.find(
      (c) =>
        c.id !== editing?.id &&
        c.assigned_driver_name === form.assigned_driver_name &&
        c.status !== "opposition"
    );
  }, [cards, form.assigned_driver_name, editing]);

  const save = async () => {
    if (!form.card_number.trim()) return toast.error("Numéro de carte requis");
    if (exactDuplicate) {
      return toast.error(
        `Ce numéro existe déjà (« ${exactDuplicate.card_number} »). Corrigez le numéro avant d'enregistrer.`
      );
    }
    if (truckConflict) {
      const ok = window.confirm(
        `Ce camion est déjà attribué à la carte « ${truckConflict.card_number} ».\n\nVoulez-vous quand même enregistrer ? Les 2 cartes resteront actives sur le même camion.`
      );
      if (!ok) return;
    }
    if (driverConflict) {
      const ok = window.confirm(
        `Ce chauffeur est déjà attribué à la carte « ${driverConflict.card_number} ».\n\nVoulez-vous quand même enregistrer ? Les 2 cartes resteront actives sur le même chauffeur.`
      );
      if (!ok) return;
    }
    const body = {
      card_number: form.card_number.trim(),
      status: form.status,
      assigned_truck_id: form.assigned_truck_id || null,
      assigned_driver_name: form.assigned_driver_name || null,
      notes: form.notes,
    };
    try {
      if (editing) await updateFuelCard(editing.id, body);
      else await createFuelCard(body);
      toast.success(editing ? "Carte mise à jour" : "Carte créée");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm("Supprimer cette carte ? (Refusé si elle a un historique)")) return;
    try {
      await deleteFuelCard(editing.id);
      toast.success("Carte supprimée");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border border-zinc-900 max-w-md">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-lg font-bold">
            <CreditCard size={20} weight="fill" className="text-amber-600" />
            {editing ? "Modifier la carte" : "Nouvelle carte"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="label-tag">Numéro de carte *</Label>
            <Input
              value={form.card_number}
              onChange={(e) => setForm({ ...form, card_number: e.target.value })}
              className={`rounded-none ${
                exactDuplicate
                  ? "border-red-600 bg-red-50"
                  : similarCards.length > 0
                  ? "border-amber-500 bg-amber-50"
                  : "border-zinc-900"
              }`}
              placeholder="Carte N°1 Total…"
              data-testid="fuel-card-number"
            />
            {exactDuplicate && (
              <div
                className="mt-2 border-2 border-red-600 bg-red-50 px-3 py-2 text-xs text-red-800 inline-flex items-center gap-2"
                data-testid="fuel-card-duplicate-warning"
              >
                <Warning size={14} weight="fill" />
                <span>
                  Une carte avec ce numéro existe déjà : «{" "}
                  <b className="mono">{exactDuplicate.card_number}</b> »
                </span>
              </div>
            )}
            {!exactDuplicate && similarCards.length > 0 && (
              <div
                className="mt-2 border-2 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                data-testid="fuel-card-similar-warning"
              >
                <div className="inline-flex items-center gap-2 font-bold">
                  <Warning size={14} weight="fill" />
                  {similarCards.length === 1
                    ? "Numéro très similaire à une carte existante :"
                    : `Numéros très similaires à ${similarCards.length} cartes existantes :`}
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {similarCards.map((c) => (
                    <span
                      key={c.id}
                      className="mono px-1.5 py-0.5 bg-amber-600 text-white text-[10px] font-bold"
                    >
                      {c.card_number}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <Label className="label-tag">Statut</Label>
            <Select
              value={form.status}
              onValueChange={(v) => setForm({ ...form, status: v })}
            >
              <SelectTrigger className="rounded-none border-zinc-900" data-testid="fuel-card-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="actif">Actif</SelectItem>
                <SelectItem value="opposition">Opposition (perdue/volée)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="label-tag">Camion assigné (par défaut)</Label>
            <TruckPicker
              value={form.assigned_truck_id}
              trucks={trucks}
              onChange={(v) => setForm({ ...form, assigned_truck_id: v })}
            />
            {form.assigned_truck_id && (
              <button
                type="button"
                onClick={() => setForm({ ...form, assigned_truck_id: "" })}
                className="mt-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-red-600"
                data-testid="fuel-card-truck-clear"
              >
                × Retirer l&apos;assignation
              </button>
            )}
            {truckConflict && (
              <div
                className="mt-2 border-2 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                data-testid="fuel-card-truck-conflict-warning"
              >
                <div className="inline-flex items-center gap-2 font-bold">
                  <Warning size={14} weight="fill" />
                  Ce camion est déjà attribué à une autre carte :
                </div>
                <div className="mt-1">
                  <span className="mono px-1.5 py-0.5 bg-amber-600 text-white text-[10px] font-bold">
                    {truckConflict.card_number}
                  </span>
                  {truckConflict.status === "opposition" && (
                    <span className="ml-2 text-red-600 font-bold">(en opposition)</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div>
            <Label className="label-tag">Chauffeur assigné (optionnel)</Label>
            <DriverPicker
              value={form.assigned_driver_name}
              drivers={drivers}
              onChange={(v) => setForm({ ...form, assigned_driver_name: v })}
            />
            {form.assigned_driver_name && (
              <button
                type="button"
                onClick={() => setForm({ ...form, assigned_driver_name: "" })}
                className="mt-1 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-red-600"
                data-testid="fuel-card-driver-clear"
              >
                × Retirer l&apos;assignation
              </button>
            )}
            {driverConflict && (
              <div
                className="mt-2 border-2 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                data-testid="fuel-card-driver-conflict-warning"
              >
                <div className="inline-flex items-center gap-2 font-bold">
                  <Warning size={14} weight="fill" />
                  Ce chauffeur est déjà attribué à une autre carte :
                </div>
                <div className="mt-1">
                  <span className="mono px-1.5 py-0.5 bg-amber-600 text-white text-[10px] font-bold">
                    {driverConflict.card_number}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div>
            <Label className="label-tag">Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="rounded-none border-zinc-900"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          {editing && (
            <button
              onClick={remove}
              className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-red-600 text-red-600 hover:bg-red-600 hover:text-white inline-flex items-center gap-2 mr-auto"
              data-testid="fuel-card-delete"
            >
              <TrashSimple size={14} weight="bold" /> Supprimer
            </button>
          )}
          <button
            onClick={onClose}
            className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-300 hover:border-zinc-900 inline-flex items-center gap-2"
          >
            <X size={14} weight="bold" /> Annuler
          </button>
          <button
            onClick={save}
            disabled={!!exactDuplicate}
            className={`px-3 py-2 text-xs uppercase tracking-widest font-bold border inline-flex items-center gap-2 ${
              exactDuplicate
                ? "border-zinc-300 bg-zinc-200 text-zinc-500 cursor-not-allowed"
                : "border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700"
            }`}
            data-testid="fuel-card-save"
          >
            {editing ? "Mettre à jour" : "Créer"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ===================== SHARED TRUCK PICKER ===================== */

function TruckPicker({ value, trucks, siteFilter = "all", onChange }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => trucks.find((t) => t.id === value), [trucks, value]);

  const { primary, others } = useMemo(() => {
    if (siteFilter === "all") return { primary: trucks, others: [] };
    const p = [];
    const o = [];
    for (const t of trucks) {
      if (t.site === siteFilter) p.push(t);
      else o.push(t);
    }
    return { primary: p, others: o };
  }, [trucks, siteFilter]);

  const renderItem = (t, offSite) => {
    const searchValue = `${t.license_plate} ${t.brand || ""} ${t.model || ""} ${t.site || ""}`.toLowerCase();
    const isSelected = t.id === value;
    return (
      <CommandItem
        key={t.id}
        value={searchValue}
        onSelect={() => {
          onChange(t.id);
          setOpen(false);
        }}
        className="rounded-none text-sm flex items-center gap-2"
        data-testid={`fuel-truck-option-${t.id}`}
      >
        <Check
          size={12}
          weight="bold"
          className={`shrink-0 ${isSelected ? "opacity-100" : "opacity-0"}`}
        />
        <span className="font-bold mono uppercase">{t.license_plate}</span>
        <span className="text-xs text-zinc-500 truncate">
          {(t.brand || "").toUpperCase()} · {(t.site || "").toUpperCase()}
        </span>
        {offSite && (
          <span className="ml-auto shrink-0 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-amber-500 text-white inline-flex items-center gap-1">
            <Warning size={9} weight="fill" /> Autre site
          </span>
        )}
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full h-10 px-3 border border-zinc-900 bg-white text-sm flex items-center justify-between gap-1 hover:border-zinc-700 text-left"
          data-testid="fuel-truck-picker"
        >
          <span className={`truncate ${selected ? "" : "text-zinc-400"}`}>
            {selected
              ? `${selected.license_plate} · ${(selected.brand || "").toUpperCase()}${
                  siteFilter !== "all" && selected.site && selected.site !== siteFilter
                    ? ` · ⚠ ${selected.site}`
                    : ""
                }`
              : "Sélectionner..."}
          </span>
          <CaretUpDown size={14} weight="bold" className="text-zinc-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-none border border-zinc-900 w-[380px]" align="start">
        <Command className="rounded-none">
          <CommandInput placeholder="Plaque, marque, modèle…" className="text-sm" />
          <CommandList>
            <CommandEmpty>Aucun camion.</CommandEmpty>
            {primary.length > 0 && (
              <CommandGroup
                heading={siteFilter !== "all" ? `Site ${siteFilter}` : "Camions"}
              >
                {primary.map((t) => renderItem(t, false))}
              </CommandGroup>
            )}
            {others.length > 0 && (
              <CommandGroup heading="⚠ Autres sites">
                {others.map((t) => renderItem(t, true))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/* ===================== SHARED DRIVER PICKER ===================== */

function DriverPicker({ value, drivers, siteFilter = "all", onChange }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => drivers.find((d) => d.name === value), [drivers, value]);

  const { primary, others } = useMemo(() => {
    if (siteFilter === "all") return { primary: drivers, others: [] };
    const p = [];
    const o = [];
    for (const d of drivers) {
      if (!d.site || d.site === "ALL" || d.site === siteFilter) p.push(d);
      else o.push(d);
    }
    return { primary: p, others: o };
  }, [drivers, siteFilter]);

  const renderItem = (d, offSite) => {
    const searchValue = `${d.name} ${d.phone || ""} ${d.site || ""}`.toLowerCase();
    const isSelected = d.name === value;
    return (
      <CommandItem
        key={d.id}
        value={searchValue}
        onSelect={() => {
          onChange(d.name);
          setOpen(false);
        }}
        className="rounded-none text-sm flex items-center gap-2"
        data-testid={`fuel-driver-option-${d.id}`}
      >
        <Check
          size={12}
          weight="bold"
          className={`shrink-0 ${isSelected ? "opacity-100" : "opacity-0"}`}
        />
        <span className="font-bold">{d.name}</span>
        {d.site && d.site !== "ALL" && (
          <span className="text-[10px] text-zinc-500 uppercase">{d.site}</span>
        )}
        {offSite && (
          <span className="ml-auto shrink-0 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-amber-500 text-white inline-flex items-center gap-1">
            <Warning size={9} weight="fill" /> Autre site
          </span>
        )}
      </CommandItem>
    );
  };

  const displayLabel = () => {
    if (!selected) return value || "Sélectionner...";
    const off =
      siteFilter !== "all" &&
      selected.site &&
      selected.site !== "ALL" &&
      selected.site !== siteFilter;
    return `${selected.name}${off ? ` · ⚠ ${selected.site}` : ""}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full h-10 px-3 border border-zinc-900 bg-white text-sm flex items-center justify-between gap-1 hover:border-zinc-700 text-left"
          data-testid="fuel-driver-picker"
        >
          <span className={`truncate ${value ? "" : "text-zinc-400"}`}>{displayLabel()}</span>
          <CaretUpDown size={14} weight="bold" className="text-zinc-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-none border border-zinc-900 w-[360px]" align="start">
        <Command className="rounded-none">
          <CommandInput placeholder="Nom du chauffeur…" className="text-sm" />
          <CommandList>
            <CommandEmpty>Aucun chauffeur.</CommandEmpty>
            {primary.length > 0 && (
              <CommandGroup
                heading={siteFilter !== "all" ? `Site ${siteFilter}` : "Chauffeurs"}
              >
                {primary.map((d) => renderItem(d, false))}
              </CommandGroup>
            )}
            {others.length > 0 && (
              <CommandGroup heading="⚠ Autres sites">
                {others.map((d) => renderItem(d, true))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


/* ===================== SHARED CARD PICKER ===================== */

function CardPicker({ value, cards, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => cards.find((c) => c.id === value), [cards, value]);

  const { actives, oppositions } = useMemo(() => {
    const a = [];
    const o = [];
    for (const c of cards) {
      if (c.status === "opposition") o.push(c);
      else a.push(c);
    }
    return { actives: a, oppositions: o };
  }, [cards]);

  const renderItem = (c, disabled) => {
    const searchValue = `${c.card_number} ${c.notes || ""}`.toLowerCase();
    const isSelected = c.id === value;
    return (
      <CommandItem
        key={c.id}
        value={searchValue}
        disabled={disabled}
        onSelect={() => {
          if (disabled) return;
          onChange(c.id);
          setOpen(false);
        }}
        className={`rounded-none text-sm flex items-center gap-2 ${disabled ? "opacity-50" : ""}`}
        data-testid={`fuel-card-option-${c.id}`}
      >
        <Check
          size={12}
          weight="bold"
          className={`shrink-0 ${isSelected ? "opacity-100" : "opacity-0"}`}
        />
        <CreditCard size={12} weight="fill" className="text-amber-600 shrink-0" />
        <span className="font-bold mono">{c.card_number}</span>
        {c.status === "opposition" && (
          <span className="ml-auto shrink-0 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-red-600 text-white">
            Opposition
          </span>
        )}
      </CommandItem>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full h-10 px-3 border border-zinc-900 bg-white text-sm flex items-center justify-between gap-1 hover:border-zinc-700 text-left"
          data-testid="fuel-entry-card"
        >
          <span className={`truncate ${selected ? "" : "text-zinc-400"}`}>
            {selected ? selected.card_number : "— Aucune —"}
          </span>
          <CaretUpDown size={14} weight="bold" className="text-zinc-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-none border border-zinc-900 w-[360px]" align="start">
        <Command className="rounded-none">
          <CommandInput placeholder="Numéro de carte…" className="text-sm" />
          <CommandList>
            <CommandEmpty>Aucune carte.</CommandEmpty>
            <CommandGroup heading="Aucune carte">
              <CommandItem
                value="__none__"
                onSelect={() => {
                  onChange("");
                  setOpen(false);
                }}
                className="rounded-none text-sm flex items-center gap-2"
                data-testid="fuel-card-option-none"
              >
                <Check
                  size={12}
                  weight="bold"
                  className={`shrink-0 ${!value ? "opacity-100" : "opacity-0"}`}
                />
                <span className="text-zinc-500 italic">— Aucune —</span>
              </CommandItem>
            </CommandGroup>
            {actives.length > 0 && (
              <CommandGroup heading="Cartes actives">
                {actives.map((c) => renderItem(c, false))}
              </CommandGroup>
            )}
            {oppositions.length > 0 && (
              <CommandGroup heading="⚠ En opposition">
                {oppositions.map((c) => renderItem(c, true))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}


/* ===================== FILTER BAR PICKERS (compact + searchable) ===================== */

function FilterTruckPicker({ value, trucks, siteFilter = "all", onChange }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => (value !== "all" ? trucks.find((t) => t.id === value) : null),
    [trucks, value]
  );

  const { primary, others } = useMemo(() => {
    if (siteFilter === "all") return { primary: trucks, others: [] };
    const p = [];
    const o = [];
    for (const t of trucks) {
      if (t.site === siteFilter) p.push(t);
      else o.push(t);
    }
    return { primary: p, others: o };
  }, [trucks, siteFilter]);

  const renderItem = (t, offSite) => {
    const searchValue = `${t.license_plate} ${t.brand || ""} ${t.model || ""} ${t.site || ""}`.toLowerCase();
    const isSelected = t.id === value;
    return (
      <CommandItem
        key={t.id}
        value={searchValue}
        onSelect={() => {
          onChange(t.id);
          setOpen(false);
        }}
        className="rounded-none text-sm flex items-center gap-2"
        data-testid={`fuel-filter-truck-option-${t.id}`}
      >
        <Check
          size={12}
          weight="bold"
          className={`shrink-0 ${isSelected ? "opacity-100" : "opacity-0"}`}
        />
        <span className="font-bold mono uppercase">{t.license_plate}</span>
        <span className="text-xs text-zinc-500 truncate">
          {(t.brand || "").toUpperCase()} · {(t.site || "").toUpperCase()}
        </span>
        {offSite && (
          <span className="ml-auto shrink-0 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-amber-500 text-white inline-flex items-center gap-1">
            <Warning size={9} weight="fill" /> Autre site
          </span>
        )}
      </CommandItem>
    );
  };

  const label = selected
    ? `${selected.license_plate}`
    : "Tous camions";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`px-2 py-1.5 text-xs font-bold uppercase tracking-widest border bg-white inline-flex items-center gap-1.5 ${
            selected
              ? "border-zinc-900 text-zinc-900"
              : "border-zinc-300 hover:border-zinc-900 text-zinc-900"
          }`}
          data-testid="fuel-filter-truck"
          title="Filtrer par camion"
        >
          <span className="mono">{label}</span>
          <CaretUpDown size={12} weight="bold" className="text-zinc-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-none border border-zinc-900 w-[340px]" align="start">
        <Command className="rounded-none">
          <CommandInput placeholder="Plaque, marque, modèle…" className="text-sm" />
          <CommandList>
            <CommandEmpty>Aucun camion.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all_trucks__"
                onSelect={() => {
                  onChange("all");
                  setOpen(false);
                }}
                className="rounded-none text-sm flex items-center gap-2"
                data-testid="fuel-filter-truck-option-all"
              >
                <Check
                  size={12}
                  weight="bold"
                  className={`shrink-0 ${value === "all" ? "opacity-100" : "opacity-0"}`}
                />
                <span className="font-bold uppercase">Tous camions</span>
              </CommandItem>
            </CommandGroup>
            {primary.length > 0 && (
              <CommandGroup
                heading={siteFilter !== "all" ? `Site ${siteFilter}` : "Camions"}
              >
                {primary.map((t) => renderItem(t, false))}
              </CommandGroup>
            )}
            {others.length > 0 && (
              <CommandGroup heading="⚠ Autres sites">
                {others.map((t) => renderItem(t, true))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function FilterCardPicker({ value, cards, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => (value !== "all" && value !== "__none__" ? cards.find((c) => c.id === value) : null),
    [cards, value]
  );

  const { actives, oppositions } = useMemo(() => {
    const a = [];
    const o = [];
    for (const c of cards) {
      if (c.status === "opposition") o.push(c);
      else a.push(c);
    }
    return { actives: a, oppositions: o };
  }, [cards]);

  const renderItem = (c) => {
    const searchValue = `${c.card_number} ${c.notes || ""}`.toLowerCase();
    const isSelected = c.id === value;
    return (
      <CommandItem
        key={c.id}
        value={searchValue}
        onSelect={() => {
          onChange(c.id);
          setOpen(false);
        }}
        className="rounded-none text-sm flex items-center gap-2"
        data-testid={`fuel-filter-card-option-${c.id}`}
      >
        <Check
          size={12}
          weight="bold"
          className={`shrink-0 ${isSelected ? "opacity-100" : "opacity-0"}`}
        />
        <CreditCard size={12} weight="fill" className="text-amber-600 shrink-0" />
        <span className="font-bold mono">{c.card_number}</span>
        {c.status === "opposition" && (
          <span className="ml-auto shrink-0 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-red-600 text-white">
            Opposition
          </span>
        )}
      </CommandItem>
    );
  };

  let label = "Toutes cartes";
  if (value === "__none__") label = "Sans carte";
  else if (selected) label = selected.card_number;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`px-2 py-1.5 text-xs font-bold uppercase tracking-widest border bg-white inline-flex items-center gap-1.5 ${
            value !== "all"
              ? "border-zinc-900 text-zinc-900"
              : "border-zinc-300 hover:border-zinc-900 text-zinc-900"
          }`}
          data-testid="fuel-filter-card"
          title="Filtrer par carte carburant"
        >
          <CreditCard size={12} weight="bold" className="text-amber-600 shrink-0" />
          <span className="mono">{label}</span>
          <CaretUpDown size={12} weight="bold" className="text-zinc-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 rounded-none border border-zinc-900 w-[340px]" align="start">
        <Command className="rounded-none">
          <CommandInput placeholder="Numéro de carte…" className="text-sm" />
          <CommandList>
            <CommandEmpty>Aucune carte.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all_cards__"
                onSelect={() => {
                  onChange("all");
                  setOpen(false);
                }}
                className="rounded-none text-sm flex items-center gap-2"
                data-testid="fuel-filter-card-option-all"
              >
                <Check
                  size={12}
                  weight="bold"
                  className={`shrink-0 ${value === "all" ? "opacity-100" : "opacity-0"}`}
                />
                <span className="font-bold uppercase">Toutes cartes</span>
              </CommandItem>
              <CommandItem
                value="__no_card__"
                onSelect={() => {
                  onChange("__none__");
                  setOpen(false);
                }}
                className="rounded-none text-sm flex items-center gap-2"
                data-testid="fuel-filter-card-option-none"
              >
                <Check
                  size={12}
                  weight="bold"
                  className={`shrink-0 ${value === "__none__" ? "opacity-100" : "opacity-0"}`}
                />
                <span className="text-zinc-600 italic">Sans carte</span>
              </CommandItem>
            </CommandGroup>
            {actives.length > 0 && (
              <CommandGroup heading="Cartes actives">
                {actives.map((c) => renderItem(c))}
              </CommandGroup>
            )}
            {oppositions.length > 0 && (
              <CommandGroup heading="⚠ En opposition">
                {oppositions.map((c) => renderItem(c))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
